<template>
	<div class="landing">
		<div class="landing__content">
			<div v-if="showIcon" class="landing__show-logo">
				<PictureImg
					:media="showIcon"
					quality="100"
					sizes="xs:285px, sm:285px, md:285px, lg:285px"
				/>
			</div>
			<div class="landing__content-inner">
				<div class="landing__buttons">
					<div
						v-if="availableLocales.length > 1"
						class="landing__languages__input"
					>
						<select
							v-model="currentLang"
							aria-label="Select language"
							:class="[
								'landing__languages__select',
								selectorColor ? selectorColor : 'default',
							]"
						>
							<option
								v-for="l in availableLocales"
								:key="`language-switcher-${l.code}`"
								:value="l.code"
							>
								{{ l.label }}
							</option>
						</select>
						<SvgIcon class="landing__languages__arrow-down" icon="arrowdown" />
					</div>
					<NuxtLinkLocale
						v-if="link === '/home/'"
						:to="link"
						:locale="currentLang"
						class="cta-btn cta-btn--light landing__btn landing__btn--redirect"
					>
						{{ confirmLabel }}
					</NuxtLinkLocale>
					<a
						v-else
						:href="link"
						class="cta-btn cta-btn--light landing__btn landing__btn--redirect"
					>
						{{ confirmLabel }}
					</a>
				</div>
			</div>
		</div>
		<PictureImg v-if="image" class="landing__img" :media="image" />
	</div>
</template>

<script setup>
const route = useRoute();
const switchLocalePath = useSwitchLocalePath();

const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
});

const showIcon = computed(() => props.fields?.showIcon?.value);
const image = computed(() => props.fields?.image?.value);
const confirmLabel = computed(() => props.fields?.confirm_label?.value);
const selectorColor = computed(() => props.fields?.selectorColor?.value);

const link = ref("/home/");

const { locales, locale } = useI18n();
const currentLang = ref(locale.value);

const availableLocales = computed(() => {
	return locales.value.map((l) => {
		return {
			label: useLangCodeTranslated(l.code),
			code: l.code,
		};
	});
});

onMounted(() => {
	watch(
		() => currentLang.value,
		(l) => {
			navigateTo(switchLocalePath(l));
		}
	);

	watch(
		[() => currentLang.value, () => route.query?.redirect],
		() => {
			if (route.query?.redirect) {
				const redirect = route.query.redirect.split("?")[0];
				const lang = currentLang.value;

				link.value = `/${lang}${redirect}`.replace("/en/", "/");
			} else {
				link.value = "/home/";
			}
		},
		{ immediate: true }
	);
});
</script>

<style lang="scss">
.landing {
	position: absolute;
	bottom: 15%;
	left: 0;
	right: 0;
	max-width: var(--app-width, 600px);
	margin: 0 auto;

	.landing__content {
		position: relative;
		z-index: 3;
		display: flex;
		flex-direction: column;
		justify-content: center;
		text-align: center;

		.page-container--echo-landing & {
			justify-content: start;
			padding-top: 10vh;
		}

		.landing__content-inner {
			width: 80%;
			margin: 0 auto;
		}
	}

	.landing__buttons {
		margin-top: 15px;
	}

	.landing__btn {
		margin-bottom: 15px;
		width: 100%;
	}

	.landing__img,
	.landing__video {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		margin: 0;
		object-fit: cover;
		object-position: center center;
		min-height: 100vh;
		min-height: var(--app-height, 100%);
		width: 100%;
	}

	.landing__img {
		z-index: 1;

		img {
			object-fit: cover;
			object-position: center center;
			height: 100%;
			width: 100%;
			margin: 0;
		}
	}

	.landing__video {
		opacity: 1;
		animation: fade-out 1s forwards;
		animation-delay: 2s;
		z-index: 2;
	}

	&.landing--has-video {
		.landing__content {
			opacity: 0;
			visibility: hidden;
			animation: fade-in 1s forwards;
			animation-delay: 3s;
		}
		.landing__img {
			opacity: 0;
			animation: fade-in 1s forwards;
			animation-delay: 3s;
		}
	}

	.landing__languages__input {
		display: inline-block;
		position: relative;
		border-radius: 30px;
		transition: all 300ms ease;
		margin-bottom: var(--spacing-1, 8px);
		min-width: 100%;
		width: 100%;
	}

	.landing__languages__select {
		-moz-appearance: none;
		-webkit-appearance: none;
		border-radius: 30px;
		display: block;
		font-family: var(--primary-font, sans-serif);
		font-size: var(--text-base, 16px);
		font-weight: 700;
		line-height: 1.2;
		min-height: 40px;
		padding: var(--spacing-1, 8px) var(--spacing-6, 48px) var(--spacing-1, 8px)
			var(--spacing-6, 48px);
		text-align-last: center;
		transition: all 200ms ease;
		width: 100%;

		&:disabled + svg {
			opacity: 0.7;
		}

		option {
			color: var(--color-grey-8, #1b1b1b);
		}

		&::-ms-expand {
			display: none;
		}

		&:hover {
			cursor: pointer;
		}

		&:hover {
			background-color: rgba(255, 255, 255, 0.35);
		}

		&:focus-visible {
			outline: 2px solid var(--color-btn-focus, #015ecc);
			outline-offset: 4px;
		}

		&.default,
		&.ghost-light {
			border: 1px solid var(--color-white, #fff);
			color: var(--color-white, #fff);
			background-color: transparent;

			& + .landing__languages__arrow-down path {
				fill: var(--color-white, #fff);
			}
		}

		&.light,
		&.standalone-light {
			border-color: var(--color-white, #fff);
			background-color: var(--color-white, #fff);
			color: #1b1b1b;

			& + .landing__languages__arrow-down path {
				fill: #1b1b1b;
			}
		}

		&.dark {
			border-color: var(--color-black, #000);
			background-color: var(--color-black, #000);
			color: var(--color-white, #fff);

			& + .landing__languages__arrow-down path {
				fill: var(--color-white, #fff);
			}
		}

		&.ghost-dark {
			background-color: transparent;
			color: var(--color-black, #000);
			border-color: var(--color-black, #000);

			& + .landing__languages__arrow-down path {
				fill: var(--color-black, #000);
			}
		}

		&.primary {
			border-color: var(--primary-color, #000);
			background-color: var(--primary-color, #000);
			color: var(--color-white, #fff);

			& + .landing__languages__arrow-down path {
				fill: var(--color-white, #fff);
			}
		}

		&.ghost-primary {
			background-color: transparent;
			color: var(--primary-color, #000);
			border-color: var(--primary-color, #000);

			& + .landing__languages__arrow-down path {
				fill: var(--primary-color, #000);
			}
		}
	}

	.landing__languages__arrow-down {
		pointer-events: none;
		position: absolute;
		right: 15px;
		top: calc(50% - 7px);
		width: 15px;
		height: 15px;

		path {
			transition: all 300ms ease;
		}
	}
}
</style>
